import debounce from 'just-debounce';

/**
 * @class FacetedFilters
 * @classdesc The faceted filters component which is included on collection and search pages to
 * allow merchants to filter lists of products dynamically. This class allows filters to be
 * updated without refreshing the page.
 *
 * @extends Core.Component
 */
export default class FacetedFilters extends Core.Component {
  constructor(el, context) {
    super(el, context);

    this.filterForm = el.querySelector('[data-filter-form]');
    this.searchInput = el.querySelector('[data-filter-search-input]');
    this.filters = el.querySelector('[data-filter-disclosures]');

    this.disclosures = el.querySelectorAll('[data-filter-disclosure]');
    this.sortBy = el.querySelector('[data-filters-sort]');
    this.resultCount = el.querySelector('[data-filter-result-count]');
    this.activeFilters = el.querySelector('[data-active-filters]');

    this.formChange = this._formChange.bind(this);

    this.events.register(this.filterForm, 'input', debounce(e => this._formChange(e), 2000));
    this.events.register(window, 'keyup', e => { if (e.key === 'Escape') this._closeAllDisclosures(); });
    this.events.register(document, 'click', e => this._closeAllDisclosures(e));
    this.disclosures.forEach(disclosure => this._setupDisclosure(disclosure));
  }

  /**
   * Main entrypoint to update all of the components of the faceted filters with any changes
   * based on the newDom.
   *
   * @param {HTMLElement} newDom - The new DOM which should include a new version of the faceted
   * filters html.
   */
  updateFilters(newDom) {
    const newFilterDom = newDom.querySelector('[data-faceted-filters]');

    if (this.searchInput && document.activeElement !== this.searchInput.querySelector('input[type="search"]')) {
      this.searchInput.innerHTML = newFilterDom.querySelector('[data-filter-search-input]').innerHTML;
    }

    if (this.resultCount) {
      this.resultCount.innerHTML = newFilterDom.querySelector('[data-filter-result-count]').innerHTML;
    }

    if (this.activeFilters) {
      const newActiveFilters = newFilterDom.querySelector('[data-active-filters]');
      this.activeFilters.innerHTML = newActiveFilters.innerHTML;
      this.activeFilters.dataset.activeFiltersCount = newActiveFilters.dataset.activeFiltersCount;

      if (newActiveFilters.dataset.activeFiltersCount === '0') {
        this.activeFilters.classList.add('hidden');
      } else {
        this.activeFilters.classList.remove('hidden');
      }
    }

    this._updateDisclosures(newFilterDom);
  }

  /**
   * Triggered when one of the filters are updated in the form.
   * Dispatches an event called `filter:update` which passes along `searchParams` from the
   * filters form.
   *
   * @param {Event} e - The event sent by the form being updated.
   * @private
   */
  _formChange(e) {
    e.preventDefault();

    const currentTarget = e.target;

    const formData = new FormData(currentTarget.closest('form'));
    const searchParams = new URLSearchParams(formData).toString();

    // Fire off event to submit the new params to the parent section
    const updateEvent = new CustomEvent('filter:update', { detail: { searchParams } });
    this.el.dispatchEvent(updateEvent);
  }

  /**
   * Updates all of the filter disclosures with a newDom.
   * Will only update disclosures if focus is not currently within the disclosure so the user does
   * not lose focus.
   *
   * @param {HTMLElement} newDom - The new DOM which should include a new version of the faceted
   * filters html.
   * @private
   */
  _updateDisclosures(newDom) {
    const currentDisclosure = document.activeElement && document.activeElement.closest('[data-filter-disclosure]');
    const existingDisclosures = this.el.querySelectorAll('[data-filter-disclosure]');
    const filterDisclosures = newDom.querySelectorAll('[data-filter-disclosure]');

    // Remove any disclosures that don't exist anymore in the new Dom
    existingDisclosures.forEach(disclosure => {
      if (!newDom.querySelector(`[data-filter-disclosure][data-index="${disclosure.dataset.index}"]`)) {
        disclosure.remove();
      }
    });

    // Update the existing Dom with the updated and new disclosures from the new Dom
    filterDisclosures.forEach(disclosure => {
      const oldDisclosure = this.filterForm.querySelector(`[data-filter-disclosure][data-index="${disclosure.dataset.index}"]`);

      if (!oldDisclosure) {
        this.filters.appendChild(disclosure);
        this._setupDisclosure(disclosure);
      } else if (!currentDisclosure || disclosure.dataset.index !== currentDisclosure.dataset.index) {
        oldDisclosure.innerHTML = disclosure.innerHTML;
      } else {
        const filterSummary = oldDisclosure.querySelector('[data-filter-summary]');
        const filterHeader = oldDisclosure.querySelector('[data-filter-header]');

        filterSummary.innerHTML = disclosure.querySelector('[data-filter-summary]').innerHTML;
        filterHeader.innerHTML = disclosure.querySelector('[data-filter-header]').innerHTML;
      }
    });

    this.disclosures = this.el.querySelectorAll('[data-filter-disclosure]');

    // Update the sort by select box
    if (this.sortBy) {
      if (!document.activeElement || !document.activeElement.closest('[data-filters-sort]')) {
        const newSort = newDom.querySelector('[data-filters-sort]');
        if (newSort) {
          this.sortBy.innerHTML = newSort.innerHTML;
        }
      }
    }
  }

  /**
   * Sets up the events to handle on the disclosure.
   *
   * @param {HTMLElement} disclosure The disclosure to be set up
   * @private
   */
  _setupDisclosure(disclosure) {
    this.events.register(disclosure, 'focusout', e => {
      // Using e.relatedTarget works best here, but the label of any checkboxes within
      // the disclosures should have a tabindex="-1" to ensure the correct target is used
      // when clicked.
      if (!disclosure.contains(e.relatedTarget)) {
        disclosure.open = false;
      }
    });
  }

  /**
   * Closes all of the open disclosures. Will keep a disclosure open if the trigger's
   * target was inside it.
   * @param {Event} event The event that is triggering the close.
   * @private
   */
  _closeAllDisclosures(event) {
    this.disclosures.forEach(disclosure => {
      if (disclosure.open) {
        if (!event || event.target.closest('[data-filter-disclosure]') !== disclosure) {
          disclosure.open = false;
          disclosure.querySelector('summary').focus();
        }
      }
    });
  }
}
